import React from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';

import { useMe, useParticipant } from '@app/api';
import { CenteredLoader, HeaderConnect } from '@app/core';
import { OnboardingProgressBar } from './OnboardingProgressBar';
import { useTranslation } from 'react-i18next';

const useStyles = createUseStyles<string, {}, Theme>((theme: Theme) => ({
  mainContainer: {
    minHeight: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.colors.grayBackground,
  },
  container: {
    position: 'relative',
    flex: 1,
    display: 'flex',
    width: '100%',
    minHeight: '100%',
    overflow: 'hidden',

    [theme.media.mobile]: {
      padding: 0,
      backgroundColor: theme.colors.white,
      overflow: 'auto',
    },
  },
  content: {
    display: 'flex',
    flexDirection: 'column',

    backgroundColor: theme.colors.white,
    minHeight: '100%',

    width: '100%',
    maxWidth: theme.maxContentWidth.screen,

    marginLeft: 'auto',
    marginRight: 'auto',
    marginTop: theme.marginBase * 5,
    marginBottom: theme.marginBase * 12,

    padding: theme.marginBase * 10,
    boxShadow: theme.boxShadow.base,
    borderRadius: theme.borderRadius.large,
    overflowY: 'visible',

    [theme.media.tablet]: {
      padding: [[theme.marginBase * 8, theme.marginBase * 4]],
      marginTop: 0,
      marginBottom: 0,
    },
    [theme.media.mobile]: {
      padding: [[theme.marginBase * 4, theme.marginBase * 3]],
      paddingTop: 0,
      boxShadow: 'none',
      maxWidth: '100%',
      borderRadius: 0,
    },
  },
  loadingPlaceholder: {
    height: 800,
  },
  contentProgress: {
    display: 'visible',
    [theme.media.mobile]: {
      display: 'none',
    },
  },
  headerProgress: {
    display: 'none',
    [theme.media.mobile]: {
      display: 'flex',
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
    },
  },
}));

interface Props {
  isLoading?: boolean;
  children?: React.ReactNode;
  currStep?: number;
}

export const OnboardingLayout = ({ isLoading, children, currStep }: Props) => {
  const theme: Theme = useTheme();
  const { t } = useTranslation();
  const classes = useStyles({ theme });
  const { data: me } = useMe(true);
  const { data: participant, isLoading: isParticipantLoading } = useParticipant(
    me?.linkedParticipants?.length ? me.linkedParticipants[0] : undefined,
  );

  const user = participant || me;

  if (isLoading || isParticipantLoading) {
    return <CenteredLoader />;
  }

  return (
    <div className={classes.mainContainer}>
      <HeaderConnect
        user={user}
        title={t('onboarding.nav')}
        bottomChildren={
          currStep !== undefined && (
            <OnboardingProgressBar
              currStep={currStep}
              className={classes.headerProgress}
            />
          )
        }
      />
      <div className={classes.container}>
        <div className={classes.content}>
          {currStep !== undefined && (
            <OnboardingProgressBar
              currStep={currStep}
              className={classes.contentProgress}
            />
          )}
          {isLoading ? (
            <div className={classes.loadingPlaceholder}>
              <CenteredLoader />
            </div>
          ) : (
            children
          )}
        </div>
      </div>
    </div>
  );
};
